import { Center, Loader } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { API } from 'services';

import { CoursePage as NewCoursePage } from './CoursePage';
import { CoursePageLegacy } from './CoursePageLegacy';

/**
 * Picks the legacy or new CoursePage based on a cutoff date.
 * Legacy is shown when the course start date is before the cutoff.
 */
export function CoursePage(props: any) {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const courseId = queryParams.get('course');
  const courseRunId = queryParams.get('course_run');

  const queryClient = useQueryClient();
  const parseCutoff = (value: string | null | undefined) => {
    if (!value) return null;
    const parsed = moment(value, moment.ISO_8601, true);
    return parsed.isValid() ? parsed.startOf('day') : null;
  };

  const courseDetail = useQuery(
    ['coursedetail', courseId],
    () =>
      API.getCourseDetail({
        course_id: courseId,
        courserun_id: courseRunId,
      }).then((res) => res.data),
    {
      enabled: Boolean(courseId && courseRunId),
    }
  );

  useEffect(() => {
    if (courseDetail.data && courseId) {
      queryClient.setQueryData(['coursedetail', courseId], courseDetail.data);
    }
  }, [courseDetail.data, courseId, queryClient]);

  if (courseDetail.isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (courseDetail.isError) {
    return <NewCoursePage {...props} />;
  }

  const courseStartRaw = courseDetail.data?.courserun_course_from;
  if (!courseStartRaw) {
    return <NewCoursePage {...props} />;
  }

  const cutoffDate = parseCutoff(courseDetail.data?.trainer_evaluation_form_cutoff_date);
  // No cutoff configured? Always show the new page.
  if (!cutoffDate) {
    return <NewCoursePage {...props} />;
  }

  const courseStart = moment(courseStartRaw).startOf('day');
  const useLegacy = courseStart.isBefore(cutoffDate);

  return useLegacy ? <CoursePageLegacy {...props} /> : <NewCoursePage {...props} />;
}
